import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import { Add } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSelector } from "react-redux";

import { IStoreState } from "../../../redux/initialStoreState";
import {
  IBusinessTask,
  INITIAL_BUSINESS_TASK,
} from "../../../redux/activity/activity.types";
import { LoadState } from "../../../constants/enums";
import { useDispatchWrapper } from "../../../hooks";
import {
  clearActivityState,
  fetchBusinessTasksAsync,
} from "../../../redux/activity/activityActions";
import { ManageBusinessTask } from "../BusinessTasks/ManageBusinessTask";
import { IActivityModuleInformation } from "../Activity.types";
import { DataTableV2 } from "../../TableV2/DataTableV2";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../TableV2/interfaces/IDataTableV2Props";
import { CustomChip } from "../../CustomChip";

interface IPolicyChangeActivitiesProps {
  moduleInformation: IActivityModuleInformation;
  customerId?: number;
  customerPolicyId?: number;
}

export const PolicyChangeActivities: React.FC<IPolicyChangeActivitiesProps> = (
  props
) => {
  const { moduleInformation, customerId, customerPolicyId } = props;
  const dispatch = useDispatchWrapper();

  const { data, dataLoading } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.tasks
  );

  const [openCreate, setOpenCreate] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState<IBusinessTask | null>(
    null
  );

  const loadActivities = React.useCallback(() => {
    dispatch(fetchBusinessTasksAsync(moduleInformation.moduleId));
  }, [dispatch, moduleInformation.moduleId]);

  React.useEffect(() => {
    loadActivities();
    return () => {
      dispatch(clearActivityState());
    };
  }, []);

  const tableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: dataLoading === LoadState.InProgress,
    tableCommandBarProps: {
      preDefinedPlugins: {
        refresh: { onClick: loadActivities },
      },
      leftItems: {
        customPlugins: [],
      },
      rightItems: {
        customPlugins: [
          {
            key: "create-activity",
            onRender: () => (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setSelectedTask(null);
                  setOpenCreate(true);
                }}
              >
                Create
              </Button>
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "task_name",
        headerName: "Task Name",
        fieldName: "task_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        enableSorting: true,
        onRowCellRender: (_value, row: IBusinessTask) => {
          return (
            <Box
              sx={{ cursor: "pointer", fontWeight: 600 }}
              color={"primary.main"}
              onClick={() => {
                setSelectedTask(row);
                setOpenCreate(true);
              }}
            >
              {row.task_name || "-"}
            </Box>
          );
        },
      },
      {
        key: "task_type",
        headerName: "Type",
        fieldName: "task_type",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "task_priority",
        headerName: "Priority",
        fieldName: "task_priority",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        onRowCellRender: (_value, row: IBusinessTask) => {
          if (!row.task_priority) return "-";
          return (
            <CustomChip
              size="small"
              color={
                (row.task_priority === "High"
                  ? "error"
                  : row.task_priority === "Medium"
                  ? "warning"
                  : "success") as any
              }
              sx={{
                fontWeight: 600,
                color: row.task_priority === "Medium" ? "#000" : "#fff",
              }}
              content={row.task_priority}
            />
          );
        },
      },
      {
        key: "assigned_to_name",
        headerName: "Assigned To",
        fieldName: "assigned_to_name",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "due_date",
        headerName: "Due Date",
        fieldName: "due_date",
        renderType: DataTableV2RowRenderType.DATE,
        enableSorting: true,
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        onRowCellRender: (_value, row: IBusinessTask) => {
          const isActive = row.status === "ACTIVE";
          return (
            <CustomChip
              size="small"
              color={(isActive ? "success" : "default") as any}
              sx={{ fontWeight: 600 }}
              content={isActive ? "Active" : "Completed"}
            />
          );
        },
      },
      {
        key: "file_upload",
        headerName: "File",
        fieldName: "file_upload",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (_value, row: IBusinessTask) => {
          if (!row.file_upload || row.file_upload.length === 0) return "-";
          return (
            <Tooltip title="File uploaded">
              <UploadFileIcon fontSize="small" />
            </Tooltip>
          );
        },
      },
      {
        key: "create_ts",
        headerName: "Created On",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        enableSorting: true,
      },
    ],
    rows: data,
  };

  return (
    <>
      <DataTableV2 {...tableProps} />

      {openCreate && (
        <ManageBusinessTask
          open={openCreate}
          task={
            selectedTask ?? {
              ...INITIAL_BUSINESS_TASK,
              module_reference_code_or_id: moduleInformation.moduleId,
            }
          }
          moduleInformation={moduleInformation}
          customer_id={customerId}
          customer_policy_id={customerPolicyId}
          onClose={() => {
            setOpenCreate(false);
            setSelectedTask(null);
          }}
          onComplete={() => {
            setOpenCreate(false);
            setSelectedTask(null);
            loadActivities();
          }}
        />
      )}
    </>
  );
};
